import { a as __rest } from '../../tslib.es6.esm.js';
import { jsx } from 'react/jsx-runtime';
import React__default from 'react';
import { StarHalfIcon } from '@iris/react-icons/StarHalfIcon';
import { StarIcon } from '@iris/react-icons/StarIcon';
import { StarSolidIcon } from '@iris/react-icons/StarSolidIcon';
import { MAX_STARS } from './constants.esm.js';
import StarRatingStyles from './StarRating.styles.esm.js';
import { cn } from '../../utils/cn.esm.js';

// Helper function to render stars
const renderStars = (variant, numSolidStars, numHalfStar, numRestStars, starClassName) => {
    if (variant === "abbreviated") {
        return jsx(StarSolidIcon, { className: starClassName }, "solid");
    }
    const starElements = [
        // render solid stars
        ...Array.from({ length: numSolidStars }, (_, i) => (jsx(StarSolidIcon, { className: starClassName }, `solid-${i}`))),
        // render half star if applicable
        ...(numHalfStar
            ? [jsx(StarHalfIcon, { className: starClassName }, "half")]
            : []),
        // render empty stars
        ...Array.from({ length: numRestStars }, (_, i) => (jsx(StarIcon, { className: starClassName }, `empty-${i}`))),
    ];
    return starElements;
};
// Helper function to generate aria-label
const generateAriaLabel = (value, maxStars) => {
    return `${value} out of ${maxStars} stars`;
};
const StarRating = React__default.forwardRef((_a, ref) => {
    var { size = "md", variant = "full", "aria-hidden": ariaHidden = true, // Default to true for abbreviated variant
    "aria-label": ariaLabel, // Accessible label for the star rating
    value = 0, className, hasVisibleLabel = false, inverse = false } = _a, props = __rest(_a, ["size", "variant", "aria-hidden", "aria-label", "value", "className", "hasVisibleLabel", "inverse"]);
    const { container, star } = StarRatingStyles({
        size,
        inverse,
    });
    // Early return for invalid values
    if (value < 0 || value > MAX_STARS) {
        return null;
    }
    // Logic to determine the number of stars based on the value
    // 4.1 and 4.2 = 4 stars
    // 4.3 to 4.7 = 4.5 stars
    // 4.8 and 4.9 = 5 stars
    const roundedValue = Math.round((value % 1) * 10) / 10; // round the value to avoid floating point issues as 4.2000000000001 > 4.2
    const numHalfStar = roundedValue >= 0.3 && roundedValue <= 0.7 ? 1 : 0; // Determine if there is a half star
    const numSolidStars = roundedValue >= 0.8 ? Math.ceil(value) : Math.floor(value); // Calculate the number of solid stars
    const numRestStars = MAX_STARS - numSolidStars - numHalfStar; // Calculate the number of empty stars
    const renderStar = renderStars(variant, numSolidStars, numHalfStar, numRestStars, star());
    const computedAriaLabel = hasVisibleLabel
        ? undefined
        : ariaLabel
            ? ariaLabel
            : generateAriaLabel(value, MAX_STARS);
    return (jsx("div", Object.assign({ ref: ref, className: cn(container(), className), "aria-hidden": variant === "full" ? hasVisibleLabel : ariaHidden, "aria-label": computedAriaLabel, role: "img" }, props, { children: renderStar })));
});
StarRating.displayName = "StarRating";

export { StarRating };
